import React from 'react';
import {
  Bar,
  BarChart,
  Area,
  AreaChart,
  Line,
  LineChart,
  CartesianGrid,
  Legend,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from 'recharts';
import { getNumericKeys, getColorForKey } from '../../utils/chartUtils';
import { CustomTooltip } from './CommonComponents';


// VELOCITY CHART COMPONENTS


/**
 * Render chart for velocity pivot data with DUAL X-AXES
 * This is the special format from TrafficSpeedInsights with both VelBucket and event_time
 */
export const VelocityPivotChart = ({ 
  data, 
  chartType, 
  compact, 
  chartMargin, 
  responsiveProps,
  xAxisProps,
  yAxisProps,
  tooltipProps,
  legendProps 
}) => {
  const numericKeys = getNumericKeys(data);

  // Determine chart configuration based on type
  const getChartConfig = () => {
    switch (chartType) {
      case "line":
        return {
          ChartComponent: LineChart,
          DataComponent: Line,
          dataProps: (key, index) => ({
            type: "monotone",
            stroke: getColorForKey(key, index),
            strokeWidth: 2,
            activeDot: { r: 6 },
            dot: { r: compact ? 2 : 3 }
          })
        };
      case "area":
        return {
          ChartComponent: AreaChart,
          DataComponent: Area,
          dataProps: (key, index) => ({
            type: "monotone",
            stroke: getColorForKey(key, index),
            fill: getColorForKey(key, index),
            fillOpacity: 0.6,
            strokeWidth: 2
          })
        };
      default: // stacked bar
        return {
          ChartComponent: BarChart,
          DataComponent: Bar,
          dataProps: (key, index) => ({
            fill: getColorForKey(key, index)
          })
        };
    }
  };

  const { ChartComponent, DataComponent, dataProps } = getChartConfig();

  return (
    <ResponsiveContainer {...responsiveProps}>
      <ChartComponent data={data} margin={chartMargin}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        
        {/* PRIMARY X-AXIS: VelBucket (Speed) */}
        <XAxis
          xAxisId="0"
          dataKey="VelBucket"
          {...xAxisProps}
          label={!compact ? {
            value: "Speed (mph)",
            position: 'insideBottom',
            offset: -5,
            style: { fontSize: 12 }
          } : undefined}
        />
        
        {/* SECONDARY X-AXIS: event_time (if available) */}
        {data[0]?.event_time && (
          <XAxis
            xAxisId="1"
            dataKey="event_time"
            allowDuplicatedCategory={false}
            tick={{ fontSize: compact ? 10 : 12 }}
          />
        )}
        
        <YAxis 
          {...yAxisProps}
          label={!compact ? {
            value: "Vehicle Count",
            angle: -90,
            position: "insideLeft",
            style: { fontSize: 12 }
          } : undefined}
        />
        
        <CustomTooltip dataFormat="velocity-pivot" {...tooltipProps} />
        
        <Legend {...legendProps} />

        {numericKeys.map((key, index) => (
          <DataComponent
            key={key}
            dataKey={key}
            name={key}
            xAxisId="0"  // Use primary X-axis (VelBucket)
            stackId={chartType === "line" ? undefined : "a"}
            {...dataProps(key, index)}
          />
        ))}
      </ChartComponent>
    </ResponsiveContainer>
  );
};

/**
 * Render chart for velocity bucket data (single X-axis)
 */
export const VelocityBucketChart = ({ 
  data, 
  chartType, 
  compact, 
  chartMargin, 
  responsiveProps,
  xAxisProps,
  yAxisProps,
  tooltipProps,
  legendProps 
}) => {
  const numericKeys = getNumericKeys(data);
  const xAxisKey = 'VelBucket';

  // Determine chart configuration
  const getChartConfig = () => {
    switch (chartType) {
      case "line":
        return {
          ChartComponent: LineChart,
          DataComponent: Line,
          dataProps: (key, index) => ({
            type: "monotone",
            stroke: getColorForKey(key, index),
            strokeWidth: 2,
            activeDot: { r: 6 },
            dot: { r: compact ? 2 : 3 }
          })
        };
      case "area":
        return {
          ChartComponent: AreaChart,
          DataComponent: Area,
          dataProps: (key, index) => ({
            type: "monotone",
            stroke: getColorForKey(key, index),
            fill: getColorForKey(key, index),
            fillOpacity: 0.6,
            strokeWidth: 2
          })
        };
      default: // stacked bar
        return {
          ChartComponent: BarChart,
          DataComponent: Bar,
          dataProps: (key, index) => ({
            fill: getColorForKey(key, index)
          })
        };
    }
  };

  const { ChartComponent, DataComponent, dataProps } = getChartConfig();

  return (
    <ResponsiveContainer {...responsiveProps}>
      <ChartComponent data={data} margin={chartMargin}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        
        <XAxis
          dataKey={xAxisKey}
          {...xAxisProps}
          label={!compact ? {
            value: "Speed (mph)",
            position: 'insideBottom',
            offset: -5,
            style: { fontSize: 12 }
          } : undefined}
        />

        <YAxis 
          {...yAxisProps}
          label={!compact ? {
            value: "Vehicle Count",
            angle: -90,
            position: "insideLeft",
            style: { fontSize: 12 }
          } : undefined}
        />
        
        <CustomTooltip dataFormat="velocity-bucket" {...tooltipProps} />
        
        <Legend {...legendProps} />

        {numericKeys.map((key, index) => (
          <DataComponent
            key={key}
            dataKey={key}
            name={key}
            stackId={chartType === "line" ? undefined : "a"}
            {...dataProps(key, index)}
          />
        ))}
      </ChartComponent>
    </ResponsiveContainer>
  );
};

/**
 * Render chart for velocity percentile data over time
 * Expects data format: { event_time, [Class]_p50, [Class]_p85, etc. }
 */
export const VelocityPercentileChart = ({ 
  data, 
  chartType, 
  compact, 
  chartMargin, 
  responsiveProps,
  xAxisProps,
  yAxisProps,
  tooltipProps,
  legendProps 
}) => {
  const numericKeys = getNumericKeys(data);
  const xAxisKey = 'event_time';

  // Default to line chart for percentiles if stacked/bar is passed by accident
  const activeChartType = chartType === 'area' ? 'area' : 'line';

  const getChartConfig = () => {
    if (activeChartType === "area") {
      return {
        ChartComponent: AreaChart,
        DataComponent: Area,
        dataProps: (key, index) => ({
          type: "monotone",
          stroke: getColorForKey(key, index),
          fill: getColorForKey(key, index),
          fillOpacity: 0.15, // Lighter fill so lines remain visible
          strokeWidth: 2
        })
      };
    }
    
    return {
      ChartComponent: LineChart,
      DataComponent: Line,
      dataProps: (key, index) => {
        // Styling logic: Emphasize 85th percentile, dash 95th percentile
        const isP85 = key.includes('p85');
        const isP95 = key.includes('p95');
        
        return {
          type: "monotone",
          stroke: getColorForKey(key, index),
          strokeWidth: isP85 ? 3 : 2,
          strokeDasharray: isP95 ? "5 5" : undefined,
          activeDot: { r: 6 },
          dot: { r: compact ? 0 : 3 }
        };
      }
    };
  };

  const { ChartComponent, DataComponent, dataProps } = getChartConfig();

  return (
    <ResponsiveContainer {...responsiveProps}>
      <ChartComponent data={data} margin={chartMargin}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        
        <XAxis
          dataKey={xAxisKey}
          {...xAxisProps}
          minTickGap={30}
          label={!compact ? {
            value: "Time",
            position: 'insideBottom',
            offset: -5,
            style: { fontSize: 12 }
          } : undefined}
        />

        <YAxis 
          {...yAxisProps}
          domain={['auto', 'auto']} // Auto-zoom Y axis for speeds
          label={!compact ? {
            value: "Speed (mph)",
            angle: -90,
            position: "insideLeft",
            style: { fontSize: 12 }
          } : undefined}
        />
        
        <CustomTooltip dataFormat="velocity-percentile" {...tooltipProps} />
        
        <Legend 
          {...legendProps} 
          formatter={(value) => value.replace('_', ' ')} // Clean up "Vehicle_p85"
        />

        {numericKeys.map((key, index) => (
          <DataComponent
            key={key}
            dataKey={key}
            name={key}
            {...dataProps(key, index)}
          />
        ))}
      </ChartComponent>
    </ResponsiveContainer>
  );
};